import React, { useEffect, useState } from 'react'
import { Route, Routes, useNavigate, Navigate } from 'react-router-dom'
import { Layout } from 'antd'
import useAuth from '../../hooks/useAuth'
import HeaderComponent from '../../components/HeaderComponent/HeaderComponent'
import ProfilePage from '../../pages/ProfilePage/ProfilePage'
import AdminCategoriesPage from '../../pages/AdminCategoriesPage/AdminCategoriesPage'
import AdminServicesPage from '../../pages/AdminServicesPage/AdminServicesPage'
import AdminLayout from '../../layouts/AdminLayout/AdminLayout'
import AdminIndexPage from '../../pages/AdminIndexPage/AdminIndexPage'
import AdminResearchesPage from '../../pages/AdminResearchesPage/AdminResearchesPage'
import { AdminResearchesCreatePage } from '../../pages/AdminResearchesPage/pages/AdminResearchesCreatePage'
import { AdminResearchPage } from '../../pages/AdminResearchesPage/pages/AdminResearchPage'
import { Layer, Profile, RootCategory } from '../../services/models'
import AdminUsersPage from '../../pages/AdminUsersPage/AdminUsersPage'
import SearchPage from '../../pages/SearchPage/SearchPage'
import AdminParserPage from '../../pages/AdminParserPage/AdminParserPage'
import FeedPage from '../../pages/FeedPage/FeedPage'
import ClientLayout from '../../layouts/ClientLayout/ClientLayout'
import userService from '../../services/userService'
import ResearchPage from '../../pages/ResearchPage/ResearchPage'
import ServicePage from '../../pages/ServicePage/ServicePage'
import { useAppDispatch } from '../../app/hooks'
import profileService from '../../services/profileService'
import { setPermItems } from '../../features/perm/permSlice'
import AdminDomainsPage from '../../pages/AdminDomainsPage/AdminDomainsPage'
import AdminLayersPage from '../../pages/AdminLayersPage/AdminLayersPage'
import AdminObjsPage from '../../pages/AdminObjsPage/AdminObjsPage'
import AdminAttributeNumbersPage from '../../pages/AdminAttributeNumbersPage/AdminAttributeNumbersPage'
import AdminAttributeDictionaryPage from '../../pages/AdminAttributeDictionaryPage/AdminAttributeDictionaryPage'
import AdminAttributeDictionaryValuesPage
  from '../../pages/AdminAttributeDictionaryValuesPage/AdminAttributeDictionaryValuesPage'
import AdminImportPage from '../../pages/AdminImportPage/AdminImportPage'
import AdminImportListPage from '../../pages/AdminImportListPage/AdminImportListPage'
import DataPage from '../../pages/DataPage/DataPage'
import DataPageStub from '../../pages/DataPage/DataPageStub'
import CatalogTreePage from '../../pages/CatalogTreePage/CatalogTreePage'
import AdminAttributeTextsPage from '../../pages/AdminAttributeTextsPage/AdminAttributeTextsPage'
import AdminPagesPage from '../../pages/AdminPagesPage/AdminPagesPage'
import { AdminPageCreatePage } from '../../pages/AdminPagesPage/pages/AdminPageCreatePage'
import { AdminPageEditPage } from '../../pages/AdminPagesPage/pages/AdminPageEditPage'
import StaticPageContent from '../../pages/StaticPage/StaticPageContent'

type Props = {
  profile: Profile
}

const AuthorizedRouter: React.FC<Props> = ({ profile }) => {
  const dispatch = useAppDispatch()

  const navigate = useNavigate()
  const { logout } = useAuth()
  const home = () => navigate('/')
  // Параметры собираются с экранированием: в «нефть & газ» или «C++» символы
  // &, # и + имеют свой смысл в строке запроса, и ручная склейка обрезала
  // запрос либо превращала его в мусорные параметры.
  // Кнопка «×» у поля поиска вызывает onSearch('') — пустой запрос ведёт на
  // чистый /search, где страница сбрасывает прошлые результаты.
  const search = (q: string) => {
    const query = q.trim()
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : '/search')
  }

  const [categories, setCategories] = useState<RootCategory[]>([])
  const [layers, setLayers] = useState<Layer[]>([])

  useEffect(() => {
    if (profile.role === 2 || profile.role === 9) {
      userService.getLayers().then(data => setLayers(data.layers))
    }
    userService.getCategories().then(data => setCategories(data.tree))
    profileService.permissions().then((data) => dispatch(setPermItems(data.data)))
  }, [])

  const routesForRole = (role: number) => {
    switch (role) {
      case 0:
        return (
          <Routes>
            <Route path="*" element={<div>ROLE_0</div>}/>
          </Routes>
        )
      case 1:
        return (
          <Routes>
            <Route path="/" element={<ClientLayout categories={categories}/>}>
              <Route index element={<Navigate to="/analytics" replace={true} />}/>
              <Route path="data" element={<DataPageStub/>}></Route>
              <Route path="feed" element={<FeedPage/>}/>
              <Route path="search" element={<SearchPage/>}/>
              <Route path="profile" element={<ProfilePage profile={profile}/>}/>
              <Route path="pages/:url" element={<StaticPageContent/>}/>
              <Route path="analytics" element={<CatalogTreePage categories={categories}/>}/>
              <Route path="analytics/:serviceId" element={<ServicePage/>}/>
              <Route path="analytics/:serviceId/research/:researchId" element={<ResearchPage/>}/>
            </Route>
          </Routes>
        )
      case 2:
        return (
          <Routes>
            <Route path="/" element={<AdminLayout role={role}/>}>
              <Route index element={<AdminIndexPage/>}/>
              <Route path="parser" element={<AdminParserPage/>}/>
              <Route path="researches" element={<AdminResearchesPage/>}/>
              <Route path="researches/create" element={<AdminResearchesCreatePage/>}/>
              <Route path="researches/:researchId" element={<AdminResearchPage/>}/>
              <Route path="categories" element={<AdminCategoriesPage/>}/>
              <Route path="services" element={<AdminServicesPage/>}/>
              <Route path="domains" element={<AdminDomainsPage/>}/>
              <Route path="layers" element={<AdminLayersPage/>}/>
              <Route path="objs/:layerId" element={<AdminObjsPage/>}/>
              <Route path="attribute-numbers" element={<AdminAttributeNumbersPage/>}/>
              <Route path="attribute-texts" element={<AdminAttributeTextsPage/>}/>
              <Route path="attribute-dictionaries" element={<AdminAttributeDictionaryPage/>}/>
              <Route path="attribute-dictionary-values/:attributeDictionaryId"
                     element={<AdminAttributeDictionaryValuesPage/>}/>
              <Route path="data" element={<DataPage layers={layers} />} />
              <Route path="pages/:url" element={<StaticPageContent/>}/>
            </Route>
            <Route path="/profile" element={<ProfilePage profile={profile}/>}/>
          </Routes>
        )
      case 9:
        return (
          <Routes>
            <Route path="/" element={<AdminLayout role={role}/>}>
              <Route index element={<AdminIndexPage/>}/>
              <Route path="parser" element={<AdminParserPage/>}/>
              <Route path="users" element={<AdminUsersPage/>}/>
              <Route path="categories" element={<AdminCategoriesPage/>}/>
              <Route path="services" element={<AdminServicesPage/>}/>
              <Route path="researches" element={<AdminResearchesPage/>}/>
              <Route path="researches/create" element={<AdminResearchesCreatePage/>}/>
              <Route path="researches/:researchId" element={<AdminResearchPage/>}/>
              <Route path="domains" element={<AdminDomainsPage/>}/>
              <Route path="layers" element={<AdminLayersPage/>}/>
              <Route path="objs/:layerId" element={<AdminObjsPage/>}/>
              <Route path="attribute-numbers" element={<AdminAttributeNumbersPage/>}/>
              <Route path="attribute-texts" element={<AdminAttributeTextsPage/>}/>
              <Route path="attribute-dictionaries" element={<AdminAttributeDictionaryPage/>}/>
              <Route path="attribute-dictionary-values/:attributeDictionaryId"
                     element={<AdminAttributeDictionaryValuesPage/>}/>
              <Route path="import" element={<AdminImportListPage/>}/>
              <Route path="import/:id" element={<AdminImportPage/>}/>
              <Route path="pages" element={<AdminPagesPage/>}/>
              <Route path="pages/create" element={<AdminPageCreatePage/>}/>
              <Route path="pages/edit/:id" element={<AdminPageEditPage/>}/>
              <Route path="data" element={<DataPage layers={layers} />} />
              <Route path="pages/:url" element={<StaticPageContent/>}/>
            </Route>
            <Route path="/profile" element={<ProfilePage profile={profile}/>}/>
          </Routes>
        )
      default:
        return (
          <Routes>
            <Route path="*" element={<h1>error: user.role {profile.role}</h1>}/>
          </Routes>
        )
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <HeaderComponent
        onHome={home}
        onLogout={logout}
        profile={profile}
        onSearch={search}
      />
      <Layout style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {routesForRole(profile.role)}
      </Layout>
      <Layout.Footer style={{ textAlign: 'center' }}>Prisma platform ©2024-{(new Date().getFullYear())}</Layout.Footer>
    </Layout>
  )
}

export default AuthorizedRouter
